import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useTelemetryStore, getDisplayStatus, gatewayOnlineForAsset, relativeTime } from "../stores/telemetryStore";
import { useUIStore } from "../stores/uiStore";
import { useSiteId } from "../hooks/useSiteId";
import { useSiteStore } from "../stores/siteStore";
import { SlotPhotoStrip } from "../components/SlotPhoto";
import { SlotCloudSection } from "../components/SlotCloud";
import { BinDivergenceSection } from "./BinDivergence";
import { api, getInventoryProducts, patchInventoryLocation } from "../api/rest";
import type { OccupancyCell } from "../scene/OccupancyLayer";
import { daysBetween } from "../scene/productSearch";
import { resolveSku } from "../pages/skuResolution";
import styles from "./Panel.module.css";

export function InspectorPanel() {
  const { t } = useTranslation();
  const selected       = useTelemetryStore((s) => s.selectedAssetId);
  const followAssetId  = useTelemetryStore((s) => s.followAssetId);
  const fork           = useTelemetryStore((s) => (selected ? s.forklifts[selected] ?? null : null));
  const gateways       = useTelemetryStore((s) => s.gateways);
  const alert          = useTelemetryStore((s) => (selected ? s.alerts[selected] ?? null : null));
  const clearAlert     = useTelemetryStore((s) => s.clearAlert);
  const clearSelection = useTelemetryStore((s) => s.clearSelection);
  const setFollow      = useTelemetryStore((s) => s.setFollow);
  const selectedBin    = useUIStore((s) => s.selectedBin);
  const setSelectedBin = useUIStore((s) => s.setSelectedBin);
  const isFollowing    = !!selected && followAssetId === selected;

  if (!selected) {
    // ADR 190: a picked storage bin (occupancy cell click). An asset click clears
    // the bin and a bin click clears the asset, so "most recent click wins" here.
    if (selectedBin) {
      return <BinInspector key={selectedBin.address} cell={selectedBin} onClose={() => setSelectedBin(null)} />;
    }
    return (
      <aside className={styles.panel} aria-label={t("a11y.inspector")}>
        <h2 className={styles.title}>{t("inspector.title")}</h2>
        <p className={styles.empty}>{t("fleet.empty")}</p>
      </aside>
    );
  }

  if (alert) {
    return (
      <aside className={styles.panel} aria-label={t("a11y.inspector")}>
        <h2 className={styles.title}>{t("inspector.alert")}</h2>
        <dl className={styles.dl}>
          <dt>{t("alerts.col.code")}</dt>
          <dd className={styles.mono}>{alert.code}</dd>
          <dt>{t("alerts.col.severity")}</dt>
          <dd>
            <span className={`${styles.pill} ${alert.severity === "CRIT" ? styles.pill_linkdown : alert.severity === "WARN" ? styles.pill_idle : styles.pill_empty}`}>
              {t(`alerts.sev.${alert.severity}`, { defaultValue: alert.severity })}
            </span>
          </dd>
          <dt>{t("common.col.asset")}</dt>
          <dd className={styles.mono}>{alert.assetId}</dd>
          <dt>{t("inspector.time")}</dt>
          <dd className={styles.mono}>{relativeTime(alert.ts)}</dd>
          <dt>{t("common.col.status")}</dt>
          <dd className={styles.mono}>{alert.status}</dd>
          {alert.detail && Object.keys(alert.detail).length > 0 && (
            <>
              <dt>{t("inspector.detail")}</dt>
              <dd className={styles.mono} style={{ fontSize: 11, wordBreak: "break-all" }}>
                {JSON.stringify(alert.detail)}
              </dd>
            </>
          )}
        </dl>
        {alert.status === "open" && (
          <button
            className={styles.btn}
            style={{ marginTop: "var(--space-3)", width: "100%" }}
            onClick={() => {
              api.ackAlert(alert.id)
                .then(() => clearAlert(alert.id))
                .catch(console.error);
            }}
          >
            {t("alerts.action.ack")}
          </button>
        )}
      </aside>
    );
  }

  if (!fork) {
    return (
      <aside className={styles.panel} aria-label={t("a11y.inspector")}>
        <h2 className={styles.title}>{t("inspector.title")}</h2>
        <p className={styles.empty}>{t("fleet.empty")}</p>
      </aside>
    );
  }

  // Edge gateway bound to this asset (commissioning visibility): the asset may have no
  // pose yet while its gateway is already up and heartbeating.
  const boundGateway = Object.values(gateways).find((g) => g.assetId === fork.id) ?? null;
  const gatewayFresh = gatewayOnlineForAsset(gateways, fork.id);

  return (
    <aside className={styles.panel} aria-label={t("a11y.inspector")}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 className={styles.title} style={{ margin: 0 }}>{t("inspector.title")}</h2>
        <button
          type="button"
          aria-label={t("a11y.closeInspector")}
          onClick={() => clearSelection()}
          style={{
            background: "transparent", color: "inherit", border: "none",
            cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "2px 6px",
          }}
        >
          ×
        </button>
      </div>
      <dl className={styles.dl}>
        <dt>{t("common.col.id")}</dt>
        <dd className={styles.mono}>{fork.id}</dd>
        <dt>{t("common.col.status")}</dt>
        <dd>
          <span className={`${styles.pill} ${styles[`pill_${getDisplayStatus(fork)}`]}`}>
            {t(`status.${getDisplayStatus(fork)}`)}
          </span>
        </dd>
        <dt>{t("common.col.speed")}</dt>
        <dd className={styles.mono}>{fork.speed.toFixed(2).replace(".", ",")} m/s</dd>
        <dt>{t("common.col.battery")}</dt>
        <dd className={styles.mono}>{Math.round(fork.battery * 100)}%</dd>
        <dt>{t("inspector.position")}</dt>
        <dd className={styles.mono}>
          x = {fork.x.toFixed(1).replace(".", ",")} m,
          y = {fork.y.toFixed(1).replace(".", ",")} m
        </dd>
        <dt>{t("inspector.load")}</dt>
        <dd>{fork.loaded ? t("status.loaded") : t("status.empty")}</dd>
        <dt>{t("common.col.lastSeen")}</dt>
        <dd className={styles.mono}>{relativeTime(fork.lastSeenMs)}</dd>
        {boundGateway && (
          <>
            <dt>{t("inspector.gateway")}</dt>
            <dd>
              <span className={`${styles.pill} ${gatewayFresh ? styles.pill_loaded : styles.pill_offline}`}>
                {gatewayFresh ? t("network.badge.online") : t("network.badge.offline")}
              </span>{" "}
              <span className={styles.mono}>{relativeTime(boundGateway.lastSeenMs)}</span>
            </dd>
          </>
        )}
      </dl>
      <button
        className={styles.btn}
        style={{ marginTop: "var(--space-3)", width: "100%" }}
        onClick={() => setFollow(isFollowing ? null : fork.id)}
      >
        {isFollowing ? t("inspector.unfollow") : t("inspector.follow")}
      </button>
      <RelocalizePanel assetId={fork.id} defaultX={fork.x} defaultY={fork.y} />
    </aside>
  );
}

/**
 * Seeds the V2Pro SLAM localization to a known map-frame pose (decisions.md#47).
 * x/y are map metres (= twin X / −Z, decisions.md#25); heading is entered in degrees
 * and sent as radians, same frame/sign as getPose.yaw.
 */
function RelocalizePanel({ assetId, defaultX, defaultY }: { assetId: string; defaultX: number; defaultY: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [x, setX] = useState(defaultX.toFixed(2));
  const [y, setY] = useState(defaultY.toFixed(2));
  const [headingDeg, setHeadingDeg] = useState("0");
  const [state, setState] = useState<"idle" | "sending" | "ok" | "error">("idle");

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "4px 6px", marginTop: 2,
    background: "var(--op-surface)", color: "var(--op-ink-900)",
    border: "1px solid var(--op-line)", borderRadius: 4, fontFamily: "monospace",
  };

  const submit = () => {
    const nx = Number(x.replace(",", ".")), ny = Number(y.replace(",", "."));
    const ndeg = Number(headingDeg.replace(",", "."));
    if (!Number.isFinite(nx) || !Number.isFinite(ny) || !Number.isFinite(ndeg)) {
      setState("error");
      return;
    }
    setState("sending");
    api.relocate(assetId, { x: nx, y: ny, angle: (ndeg * Math.PI) / 180 })
      .then(() => setState("ok"))
      .catch(() => setState("error"));
  };

  if (!open) {
    return (
      <button
        className={styles.btn}
        style={{ marginTop: "var(--space-2)", width: "100%" }}
        onClick={() => { setOpen(true); setState("idle"); }}
      >
        {t("inspector.relocate")}
      </button>
    );
  }

  return (
    <div style={{ marginTop: "var(--space-2)", padding: "var(--space-2)", border: "1px solid var(--op-line)", borderRadius: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <strong style={{ fontSize: 13 }}>{t("inspector.relocateTitle")}</strong>
        <button type="button" aria-label={t("a11y.close")} onClick={() => setOpen(false)}
          style={{ background: "transparent", color: "inherit", border: "none", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
      </div>
      <label style={{ fontSize: 11 }}>{t("inspector.relocateX")}
        <input style={inputStyle} value={x} inputMode="decimal" onChange={(e) => setX(e.target.value)} />
      </label>
      <label style={{ fontSize: 11, display: "block", marginTop: 6 }}>{t("inspector.relocateY")}
        <input style={inputStyle} value={y} inputMode="decimal" onChange={(e) => setY(e.target.value)} />
      </label>
      <label style={{ fontSize: 11, display: "block", marginTop: 6 }}>{t("inspector.heading")}
        <input style={inputStyle} value={headingDeg} inputMode="decimal" onChange={(e) => setHeadingDeg(e.target.value)} />
      </label>
      <button className={styles.btn} style={{ marginTop: 8, width: "100%" }}
        disabled={state === "sending"} onClick={submit}>
        {state === "sending" ? t("inspector.sending") : t("inspector.confirmPose")}
      </button>
      {state === "ok" && <p style={{ fontSize: 11, color: "var(--op-success)", marginTop: 6 }}>{t("inspector.poseSent")}</p>}
      {state === "error" && <p style={{ fontSize: 11, color: "var(--op-danger)", marginTop: 6 }}>{t("inspector.poseError")}</p>}
    </div>
  );
}

/** Metres with the pt-BR decimal comma ("2,05"). */
function metres(v: number): string {
  return v.toFixed(2).replace(".", ",");
}

/**
 * Occupancy fill color by value (ADR 206): <50% red, <80% amber, otherwise
 * green (over-height stays green). Returns a token `var()` applied INLINE, not
 * a CSS-module class — a dynamic `styles[tier]` lookup resolved to undefined on
 * a clean load and left a transparent fill on a light track (an invisible bar).
 * Exported so the thresholds are unit-tested without touching the DOM.
 */
export function occupancyTierColor(pct: number): string {
  if (pct < 50) return "var(--op-danger)";
  if (pct < 80) return "var(--op-warn)";
  return "var(--op-success)";
}

const BIN_PILL: Record<OccupancyCell["status"], string> = {
  counted: "pill_loaded",
  empty: "pill_empty",
  not_scanned: "pill_unvisited",
  unidentified: "pill_linkdown",
};

/**
 * ADR 190: the storage-bin branch of the inspector — what the latest run read at
 * this address, plus the ADR 186 per-location height edit. The cell snapshot comes
 * from the occupancy layer's click; after a save the scene refetches (bumpSceneData)
 * while this panel keeps echoing the values it just wrote.
 */
function BinInspector({ cell, onClose }: { cell: OccupancyCell; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const siteId = useSiteId();
  const warehouseId = useSiteStore((s) => s.activeWarehouseId);
  // Dwell anchor (ADR 203): one date per mount — same convention as the finder.
  const [todayIso] = useState(() => new Date().toISOString().slice(0, 10));
  const day = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString(i18n.language);

  // ADR 275: the WMS catalogue, for the description. Absent snapshot → empty
  // list, exactly as the ADR 196 product search degrades: code only, never an
  // error and never a retry loop.
  const { data: catalog } = useQuery({
    queryKey: ["inventory-products", siteId, warehouseId],
    queryFn: () => getInventoryProducts(siteId, warehouseId!),
    enabled: !!siteId && !!warehouseId,
    staleTime: 300_000,
    retry: false,
  });
  const descriptions = useMemo(
    () => new Map((catalog ?? []).map((r) => [r.product, r.description])),
    [catalog],
  );

  // One rule, shared with the run report (ADR 275) so the two cannot drift.
  const sku = resolveSku(cell.product, cell.item, (c) => descriptions.has(c));
  // ADR 406: the scanned code is always shown as the label, even when it is the
  // SKU too — an operator checks the panel against the sticker on the pallet.
  const label = cell.product || null;
  const description = sku ? descriptions.get(sku) ?? null : null;
  const deep = cell.depthSlots === 2 && !!cell.pallets;

  return (
    <aside className={styles.panel} aria-label={t("a11y.inspector")}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 className={styles.title} style={{ margin: 0 }}>{t("inspector.bin.title")}</h2>
        <button
          type="button"
          aria-label={t("a11y.closeInspector")}
          onClick={onClose}
          style={{
            background: "transparent", color: "inherit", border: "none",
            cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "2px 6px",
          }}
        >
          ×
        </button>
      </div>
      <dl className={styles.dl}>
        <dt>{t("inventory.items.col.address")}</dt>
        <dd className={styles.mono}>{cell.address}</dd>
        <dt>{t("common.col.status")}</dt>
        <dd>
          <span className={`${styles.pill} ${styles[BIN_PILL[cell.status]]}`}>
            {t(`inventory.item.${cell.status}`)}
          </span>
        </dd>
        {/* ADR 425: a double-deep bin names each position — front (aisle side)
            and back — with its own pallet, the way the 3D view draws them. */}
        {deep && (["front", "back"] as const).map((depth) => {
          const p = cell.pallets?.find((x) => x.depth === depth);
          const pSku = p ? resolveSku(p.product, p.item, (c) => descriptions.has(c)) : null;
          return (
            <Fragment key={depth}>
              <dt>{t(`inspector.bin.depth.${depth}`)}</dt>
              <dd data-testid={`depth-${depth}`} style={{ overflowWrap: "anywhere" }}>
                {p ? (
                  <>
                    <span className={`${styles.pill} ${styles[p.inferred ? "pill_inferred" : BIN_PILL[p.status]]}`}>
                      {p.inferred ? t("inspector.bin.depth.inferred") : t(`inventory.item.${p.status}`)}
                    </span>
                    {pSku && <span className={styles.mono}> {pSku}</span>}
                    {/* ADR 428: an inferred pallet carries the front's label; the pill says so. */}
                    {p.product && p.product !== pSku && !p.inferred && <span className={styles.mono}> · {p.product}</span>}
                    {p.quantity != null && <span className={styles.mono}> · {p.quantity}</span>}
                    {pSku && descriptions.get(pSku) && <div>{descriptions.get(pSku)}</div>}
                  </>
                ) : t("inspector.bin.depth.empty")}
              </dd>
            </Fragment>
          );
        })}
        {/* SKU → description → the scanned pallet label, in that order: the
            question is "what is in here", and the barcode is provenance for the
            answer rather than the answer itself (ADR 275). */}
        {!deep && sku && (
          <>
            <dt>{t("inspector.bin.sku")}</dt>
            <dd className={styles.mono}>{sku}</dd>
          </>
        )}
        {!deep && description && (
          <>
            <dt>{t("inspector.bin.description")}</dt>
            <dd>{description}</dd>
          </>
        )}
        {!deep && label && (
          <>
            <dt>{t("inspector.bin.label")}</dt>
            <dd className={styles.mono}>{label}</dd>
          </>
        )}
        {!deep && cell.quantity != null && (
          <>
            <dt>{t("inspector.bin.quantity")}</dt>
            <dd className={styles.mono}>{cell.quantity}</dd>
          </>
        )}
        {cell.level != null && (
          <>
            <dt>{t("inspector.bin.level")}</dt>
            <dd className={styles.mono}>{cell.level}</dd>
          </>
        )}
        {/* ADR 406: the elevation stays on the cell (the rack is drawn from it and the
            height editor below curates it) but is not a row here — an operator reads
            level and clear height; metres above the slab decide nothing on the floor. */}
        {/* Allocation height → cargo height → occupancy, read as one group: how
            much of this position's vertical allocation the measured load uses. */}
        {cell.clearHeightM != null && (
          <>
            <dt>{t("inspector.bin.clearHeight")}</dt>
            <dd className={styles.mono}>{metres(cell.clearHeightM)} m</dd>
          </>
        )}
        {cell.loadHeightM != null && (
          <>
            <dt>{t("inspector.bin.loadHeight")}</dt>
            <dd className={styles.mono}>{metres(cell.loadHeightM)} m</dd>
          </>
        )}
        {cell.loadHeightM != null && cell.clearHeightM != null && cell.clearHeightM > 0 && (() => {
          const pct = (cell.loadHeightM / cell.clearHeightM) * 100;
          const color = occupancyTierColor(pct);
          return (
            <>
              <dt>{t("inspector.bin.occupancy")}</dt>
              <dd>
                <span className={styles.mono} style={{ color }}>{Math.round(pct)}%</span>
                {/* The fill caps at 100% but the number does not: an over-height
                    load reading "120%" is a real operational signal, not noise. */}
                <div className={styles.meter}>
                  <div
                    data-testid="occupancy-fill"
                    className={styles.meterFill}
                    style={{ width: `${Math.min(100, pct)}%`, background: color }}
                  />
                </div>
              </dd>
            </>
          );
        })()}
        {/* ADR 203: what the WMS snapshot says about this ADDRESS — shown on any
            scan status, it's the same enrichment the finder's predicates read. */}
        {cell.fifoDate && (
          <>
            <dt>{t("inspector.bin.dwell")}</dt>
            <dd className={styles.mono}>
              {t("inspector.bin.dwellDays", { count: daysBetween(cell.fifoDate, todayIso) })}
            </dd>
          </>
        )}
        {cell.mfgDate && (
          <>
            <dt>{t("inspector.bin.mfg")}</dt>
            <dd className={styles.mono}>{day(cell.mfgDate)}</dd>
          </>
        )}
        {cell.expiryDate && (
          <>
            <dt>{t("inspector.bin.expiry")}</dt>
            <dd className={styles.mono}>{day(cell.expiryDate)}</dd>
          </>
        )}
      </dl>
      {/* ADR 287: the divergence lens (ADR 283) at the address — visible and
          justifiable here, not only on the run report. */}
      {warehouseId && (
        <BinDivergenceSection
          siteId={siteId}
          warehouseId={warehouseId}
          address={cell.address}
          runId={cell.runId}
        />
      )}
      {/* ADR 272: the evidence for what the numbers above describe. Rendered
          last because it answers the question the rest of the panel provokes —
          "so what IS in there?" — and only appears when a frame exists. */}
      {warehouseId && (
        <>
          <SlotPhotoStrip siteId={siteId} warehouseId={warehouseId} address={cell.address} />
          {/* ADR 284: the stitched dual-camera capture of what stands here —
              absent (not empty) for the many addresses without one. */}
          <SlotCloudSection siteId={siteId} warehouseId={warehouseId} address={cell.address} />
        </>
      )}
      {cell.id && <BinHeightsEditor cell={cell} locationId={cell.id} />}
    </aside>
  );
}

/**
 * ADR 186 tier 2, shipped here (ADR 190): edit one location's measured heights.
 * Values prefill from the cell's EFFECTIVE geometry (what is on screen); saving
 * writes the location columns — which by the specific-over-generic rule then BEAT
 * the level curation/nominal and reshape both the prism and the paint on refetch.
 */
function BinHeightsEditor({ cell, locationId }: { cell: OccupancyCell; locationId: string }) {
  const { t } = useTranslation();
  const siteId = useSiteId();
  const bumpSceneData = useUIStore((s) => s.bumpSceneData);
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState(cell.level != null ? String(cell.level) : "");
  const [elevation, setElevation] = useState(cell.elevationM != null ? metres(cell.elevationM) : "");
  const [clear, setClear] = useState(cell.clearHeightM != null ? metres(cell.clearHeightM) : "");
  const [state, setState] = useState<"idle" | "sending" | "ok" | "error">("idle");

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "4px 6px", marginTop: 2,
    background: "var(--op-surface)", color: "var(--op-ink-900)",
    border: "1px solid var(--op-line)", borderRadius: 4, fontFamily: "monospace",
  };

  const submit = () => {
    const num = (s: string) => (s.trim() === "" ? undefined : Number(s.replace(",", ".")));
    const [nLevel, nElev, nClear] = [num(level), num(elevation), num(clear)];
    const bad = (v: number | undefined) => v != null && !Number.isFinite(v);
    if (bad(nLevel) || bad(nElev) || bad(nClear)
      || (nLevel != null && (!Number.isInteger(nLevel) || nLevel < 1))
      || (nElev != null && nElev < 0)
      || (nClear != null && nClear <= 0)
      || (nLevel == null && nElev == null && nClear == null)) {
      setState("error");
      return;
    }
    setState("sending");
    patchInventoryLocation(siteId, locationId, {
      ...(nLevel != null ? { level: nLevel } : {}),
      ...(nElev != null ? { elevationM: nElev } : {}),
      ...(nClear != null ? { clearHeightM: nClear } : {}),
    })
      .then(() => { setState("ok"); bumpSceneData(); })
      .catch(() => setState("error"));
  };

  if (!open) {
    return (
      <button
        className={styles.btn}
        style={{ marginTop: "var(--space-3)", width: "100%" }}
        onClick={() => { setOpen(true); setState("idle"); }}
      >
        {t("inspector.bin.edit")}
      </button>
    );
  }

  return (
    <div style={{ marginTop: "var(--space-2)", padding: "var(--space-2)", border: "1px solid var(--op-line)", borderRadius: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <strong style={{ fontSize: 13 }}>{t("inspector.bin.editTitle")}</strong>
        <button type="button" aria-label={t("a11y.close")} onClick={() => setOpen(false)}
          style={{ background: "transparent", color: "inherit", border: "none", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
      </div>
      <label style={{ fontSize: 11 }}>{t("inspector.bin.level")}
        <input style={inputStyle} value={level} inputMode="numeric" onChange={(e) => setLevel(e.target.value)} />
      </label>
      <label style={{ fontSize: 11, display: "block", marginTop: 6 }}>{t("inspector.bin.elevationInput")}
        <input style={inputStyle} value={elevation} inputMode="decimal" onChange={(e) => setElevation(e.target.value)} />
      </label>
      <label style={{ fontSize: 11, display: "block", marginTop: 6 }}>{t("inspector.bin.clearHeightInput")}
        <input style={inputStyle} value={clear} inputMode="decimal" onChange={(e) => setClear(e.target.value)} />
      </label>
      <button className={styles.btn} style={{ marginTop: 8, width: "100%" }}
        disabled={state === "sending"} onClick={submit}>
        {state === "sending" ? t("inspector.bin.saving") : t("inspector.bin.save")}
      </button>
      {state === "ok" && <p style={{ fontSize: 11, color: "var(--op-success)", marginTop: 6 }}>{t("inspector.bin.saved")}</p>}
      {state === "error" && <p style={{ fontSize: 11, color: "var(--op-danger)", marginTop: 6 }}>{t("inspector.bin.error")}</p>}
    </div>
  );
}
